import { useEffect, useId, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Centred dialog, for create/confirm flows only.
 *
 * Deliberately NOT used for record detail — a centred modal over a data grid
 * destroys the list context, which is why detail views use `Drawer`. Creating a
 * role has no list context to preserve, so a modal is right there.
 */
export function Modal({
  open, onClose, title, description, children, footer, width = 'md',
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: 'sm' | 'md' | 'lg'
}) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  /**
   * Focus: move it in, keep it in, put it back.
   *
   * The backdrop is a full-bleed `<button aria-label="Close">` and it is FIRST in
   * the DOM, so without this the first Tab inside any dialog lands on it and the
   * first Enter closes the dialog the person was trying to fill in. Everything
   * here is one behaviour with three halves — take focus, trap it, restore it —
   * and splitting them across effects is how one of them gets deleted.
   */
  useEffect(() => {
    if (!open) return
    const returnTo = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    const focusables = () => Array.from(
      panel?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),'
        + ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    ).filter((el) => el.offsetParent !== null)

    // The first real control, not the close button — a dialog that opens with
    // focus on Close invites closing it.
    const first = focusables().find((el) => el.getAttribute('aria-label') !== 'Close dialog')
    ;(first ?? panel)?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const firstEl = items[0]!
      const lastEl = items[items.length - 1]!
      const active = document.activeElement
      if (e.shiftKey && (active === firstEl || !panel?.contains(active))) {
        e.preventDefault(); lastEl.focus()
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault(); firstEl.focus()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      returnTo?.focus?.()
    }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
         role="dialog" aria-modal="true" aria-labelledby={titleId}>
      {/*
        The scrim is decorative and must not be a tab stop: it sits first in the
        DOM, so as a focusable button it was the first thing Tab reached inside
        every dialog. The close button in the header does the same job with a
        name and a position that make sense.
      */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"
        aria-hidden="true"
        onClick={onClose}
      />
      <div ref={panelRef} tabIndex={-1} className={cn(
        'relative bg-background border border-border rounded-t-md sm:rounded-md shadow-xl',
        'w-full flex flex-col max-h-[92vh] sm:max-h-[85vh]',
        width === 'sm' && 'sm:max-w-md',
        width === 'md' && 'sm:max-w-xl',
        width === 'lg' && 'sm:max-w-3xl',
      )}>
        <header className="px-4 py-3 border-b border-border bg-muted/30 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h3 id={titleId} className="text-erp-md font-semibold tracking-tight">{title}</h3>
            {description && (
              <p className="text-erp-xs text-muted-foreground mt-0.5">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain p-4">{children}</div>

        {footer && (
          <footer className="px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-end gap-2">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}

/** Field wrapper with label, hint and error, at the ported control sizes. */
/**
 * A labelled form control.
 *
 * The `<label>` **wraps** the control rather than sitting beside it. That is not a
 * layout preference: a `<label>` with no `htmlFor` and no child input labels nothing, so
 * every field built this way was invisible to `getByLabel`, to a screen reader, and to
 * anyone clicking the word expecting focus to land in the box. Wrapping gives the
 * association implicitly and needs no id to be threaded through every caller.
 *
 * The error and the hint stay outside the label — they are description, not name, and a
 * screen reader that reads them as part of the label reads the whole paragraph before
 * the user can type.
 */
export function Field({
  label, hint, error, required, children,
}: {
  label: string
  hint?: string
  error?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <label className="block space-y-1.5">
        <span className="text-erp-xs font-medium text-muted-foreground block">
          {label}
          {required && <span className="text-red-600 ml-0.5">*</span>}
        </span>
        {children}
      </label>
      {error
        ? <p className="text-erp-xs text-red-700">{error}</p>
        : hint ? <p className="text-erp-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}
